<script>
import {defineComponent} from 'vue';
import {EMAIL_RE} from '../../defs';

const SCREEN = {
    Input: 'input',
    Manage: 'manage',
    Error: 'error',
    Success: 'success',
};

export default defineComponent({

    name: 'SendlistApp',

    props: {
        title: {
            type: String,
            default: 'Nyhetsbrev',
        },
        titleTag: {
            type: String,
            default: 'h2',
        },
        subtitle: { type: String },
        description: { type: String },
        imageUrl: { type: String },
        imageHtml: { type: String },
    },

    data() {
        return {
            loading: null,
            SCREEN,
            screenHistory: [SCREEN.Input],

            email: '',

            subId: '',
            userSubscriptionGroups: [],
            subscriptionGroups: [],

            errorText: '',
            successText: '',
        };
    },

    computed: {
        currentScreen() {
            return this.screenHistory[0];
        },
        isEmailValid() {
            return !!(this.email && this.email.match(EMAIL_RE));
        },
    },

    mounted() {
        const slugMatch = location.search.match(/[&?]slug=([\w\d]{40})/);
        slugMatch && this.initSubscription(slugMatch[1]);
    },

    methods: {

        setScreen(screen) {
            this.screenHistory.unshift(screen);
        },

        isScreen(screen) {
            return this.screenHistory[0] === screen;
        },

        tryAgain() {
            const nonErrorIndex = this.screenHistory.findIndex(screen => screen !== SCREEN.Error);
            this.screenHistory = this.screenHistory.slice(nonErrorIndex);
            if (!this.screenHistory.length) {
                this.screenHistory = this.screenHistory.concat(SCREEN.Input);
            }
        },

        toggleUserSubscriptionGroup(group) {
            if (this.userSubscriptionGroups.some(g => g.id === group.id)) {
                this.userSubscriptionGroups = this.userSubscriptionGroups.filter(g => g.id !== group.id);
            } else {
                this.userSubscriptionGroups = this.userSubscriptionGroups.concat(group).sort((a, b) => {
                    return a.id > b.id ? 1 : (a.id < b.id ? -1 : 0);
                });
            }
        },

        async initSubscription(subId) {
            this.subId = subId;

            this.loading = 'userSubscription';
            const resp = (await this.api(`user/${subId}/subscription`));
            if (resp?.data) {

                this.email = resp.data.email;
                this.userSubscriptionGroups = Object.keys(resp.data.subscriptions?.groups || {}).map(id => {
                    const group = resp.data.subscriptions.groups[id];
                    group.id = parseInt(group.id);
                    return group;
                });

                this.loading = 'subscriptions';
                const listResp = await this.api('subscriptions');
                if (listResp?.groups) {
                    this.subscriptionGroups = listResp.groups;
                    this.subscriptionGroups.forEach(group => {
                        group.id = parseInt(group.id);
                    });
                    this.setScreen(SCREEN.Manage);
                }
            }

            if (!this.isScreen(SCREEN.Manage)) {
                this.setScreen(SCREEN.Error);
                this.errorText = 'Vi hittar ingen prenumerant';
            }

            this.loading = null;
        },

        async createSubscription(email) {
            this.loading = 'createSubscription';
            const resp = await this.api('user/subscribe', {
                method: 'POST',
                body: JSON.stringify({ email }),
                headers: {
                    'X-HTTP-Method-Override': 'PUT',
                },
            });
            if (resp) {
                this.setScreen(SCREEN.Success);
                this.successText = 'Du kommer nu få ett mail med en länk som leder dig till sidan där du kan bestämma vilka nyhetsbrev du vill prenumerera på';
            } else if (this.errorText.indexOf('sending link to subscriber') >= 0) {
                this.setScreen(SCREEN.Success);
                this.successText = 'Vi skickar ett mail med en länk där du kan hantera dina prenumerationer';
            } else {
                this.setScreen(SCREEN.Error);
                this.errorText = 'Vi kunde tyvärr inte lägga till din e-post';
            }
            this.loading = null;
        },

        async updateSubscription(subId, subscriptionGroups) {
            this.loading = 'updateSubscription';
            const resp = await this.api(`user/${subId}/subscription`, {
                method: 'POST',
                body: JSON.stringify({ groups: subscriptionGroups.map(g => g.id) }),
            });
            if (resp) {
                this.setScreen(SCREEN.Success);
                this.successText = 'Dina prenumerationer sparades';
            } else {
                this.setScreen(SCREEN.Error);
                this.errorText = 'Kunde inte spara ändringarna';
            }
            this.loading = null;
        },

        async cancelSubscription(subId) {
            if (!window.confirm(`Är du säker att du vill säga upp din prenumeration?`)) {
                return;
            }
            this.loading = 'cancelSubscription';
            const resp = await this.api(`user/${subId}/unsubscribe`, {
                method: 'POST',
                headers: {
                    'X-HTTP-Method-Override': 'DELETE',
                },
            });
            if (resp) {
                this.setScreen(SCREEN.Success);
                this.successText = 'Din e-post är avanmäld';
            } else {
                this.errorText = 'Vi kunde tyvärr inte ta bort din e-post';
            }
            this.loading = null;
        },

        /**
         *
         * @param {string} endpoint
         * @param {RequestInit} args
         * @returns {Promise<object>}
         */
        async api(endpoint, args = {}) {
            try {
                const epoint = endpoint.charAt(0) === '/' ? endpoint.slice(1) : endpoint;
                const response = await (await fetch(`/wp-json/liberalerna/v1/sendlist/${epoint}`, Object.assign({}, args, {
                    headers: Object.assign({
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                    }, args.headers),
                }))).json();
                if (!response.success && response.message) {
                    throw new Error(response.message);
                } else {
                    return response;
                }
            } catch (err) {
                console.warn(err);
                this.setScreen(SCREEN.Error);
                this.errorText = err.message;
                this.loading = null;
            }
            return null;
        },
    },
});

</script>

<template>
    <div class="sendlist-app">
        <div class="sendlist-content">
            <h2 :is="titleTag">{{ title }}</h2>

            <transition name="loading">
                <div v-if="loading" class="sendlist-loading">
                    <svg>
                        <use xlink:href="#icon-loading-bars"/>
                    </svg>
                </div>
            </transition>

            <div v-if="isScreen(SCREEN.Error)" class="sendlist-failure">
                <h3><span class="icon icon-warning"/> Något gick fel</h3>
                <p>{{ errorText }}</p>
                <div class="sendlist-actionButtons">
                    <button class="lbrl-btn is--hollow" @click="tryAgain">Försök igen</button>
                </div>
            </div>

            <div v-else-if="isScreen(SCREEN.Success)" class="sendlist-success">
                <p><span class="icon icon-checked"/>{{ successText }}</p>
            </div>

            <div v-else-if="isScreen(SCREEN.Manage)" class="sendlist-manage">
                <h3>Inställningar för {{ email }}</h3>
                <ul class="sendlist-choices">
                    <li v-for="group in subscriptionGroups" :key="group.id">
                        <label>
                            <input
                                type="checkbox"
                                :value="group.id"
                                :checked="userSubscriptionGroups.some(g => g.id === group.id)"
                                @change="toggleUserSubscriptionGroup(group)"
                            />
                            <span>{{ group.title }}</span><br/>
                            <span>{{ group.description }}</span>
                        </label>
                    </li>
                </ul>
                <div class="sendlist-actionButtons">
                    <button
                        class="sendlist-updateBtn lbrl-btn"
                        @click="updateSubscription(subId, userSubscriptionGroups || [])"
                    >Spara inställningar
                    </button>
                    <button class="sendlist-cancelBtn" @click="cancelSubscription(subId)"
                    >Avbryt prenumeration
                    </button>
                </div>
            </div>

            <div v-else class="sendlist-input">
                <h3 v-if="subtitle" v-html="subtitle"></h3>
                <div v-if="description" v-html="description"></div>
                <form class="sendlist-email" @submit.prevent="createSubscription(email)">
                    <label>
                        <input type="email" v-model="email" :class="{'has--value': email}"/>
                        <span>Din epostadress</span>
                    </label>
                    <button :disabled="!isEmailValid">Registrera<span class="icon icon-arrow-right"/></button>
                </form>
            </div>

        </div>

        <div v-if="imageHtml" class="sendlist-media" v-html="imageHtml"></div>
        <div v-else class="sendlist-media">
            <img :src="imageUrl" alt/>
        </div>
    </div>
</template>
